<script lang="ts">
  import Button from '@/lib/components/ui/button/button.svelte';
  import { cn } from '@/lib/utils';
  import { autoGenWorkout, type XSSTargetsResponse } from '@/Util/api';
  import { targetsLowIntensity, toDateInputString, toTimeInputString } from '@/Util/utils';
  import Separator from '@/lib/components/ui/separator/separator.svelte';
  import { MagicBucketsFieldsAndData } from '../MagicBuckets/MagicBucketsFieldsAndData';
  import MagicBucketsSimplified from '../MagicBuckets/MagicBucketsSimplified.svelte';
  import * as Dialog from '$lib/components/ui/dialog';
  import MagicBucketsGenerateWorkout from '../MagicBuckets/MagicBucketsGenerateWorkout.svelte';
  import { activitiesForDate } from '@/Singletons/ActivitiesForDateWritable';
  import { Activity } from '@/Common/ActivityFunctions';
  import * as Popover from '$lib/components/ui/popover/index.js';
  import { buttonVariants } from '@/lib/components/ui/button';
  import { Label } from '@/lib/components/ui/label';
  import { Input } from '@/lib/components/ui/input';

  export let xssTargetsResponse: XSSTargetsResponse = null;
  export let magicBucketsFieldsAndData: MagicBucketsFieldsAndData = null;
  export let date: Date;

  let showChart: boolean = true; // Have a "generate" button that opens a dialog, or a "schedule" button directly

  let workoutDialogOpen = false;
  let workoutSchedulePopoverOpen = false;
  $: inputDate = toDateInputString(date);
  $: inputTime = toTimeInputString(new Date());
  $: forecastActivity = $activitiesForDate?.activities?.find((a) => Activity.isForecastEvent({ ...a, state: a.state }));

  $: targetsAreLowIntensity = targetsLowIntensity(xssTargetsResponse?.remainingTargetsNumeric);
  $: targetsAreZero = xssTargetsResponse?.remainingTargetsNumeric?.xss <= 0;

  async function scheduleMagicWorkout(dateTime: Date) {
    // if (!magicBucketsFieldsAndData.mpaData) return;
    await autoGenWorkout({
      forDate: dateTime,
      bucketsInfo: magicBucketsFieldsAndData.mpaData ? { intervals: magicBucketsFieldsAndData.mpaData.intervals, challengeLevel: magicBucketsFieldsAndData.selectedChallengeLevel } : null,
      targets: xssTargetsResponse?.remainingTargetsNumeric,
    });

    workoutDialogOpen = false;
  }

  async function assignMagicWorkoutToForecast() {
    if (!forecastActivity) return;

    let dateTime = new Date(forecastActivity.start_date);
    let editId = forecastActivity.path;

    await autoGenWorkout({
      forDate: dateTime,
      edit_id: editId,
      bucketsInfo: magicBucketsFieldsAndData.mpaData ? { intervals: magicBucketsFieldsAndData.mpaData.intervals, challengeLevel: magicBucketsFieldsAndData.selectedChallengeLevel } : null,
      targets: xssTargetsResponse?.remainingTargetsNumeric,
    });

    workoutDialogOpen = false;
  }
</script>

<MagicBucketsSimplified bind:fields={magicBucketsFieldsAndData} targetsSource={xssTargetsResponse?.source} bind:showChart />

<Separator orientation="horizontal" class="my-4" />

<div class="flex flex-row flex-wrap gap-2">
  {#if showChart}
    {#if forecastActivity}
      <Button disabled={targetsAreZero} variant="outline" on:click={assignMagicWorkoutToForecast}>Assign to Forecast</Button>
    {:else}
      <Popover.Root bind:open={workoutSchedulePopoverOpen} portal="#shadcn-ui-dialog-target">
        <Popover.Trigger asChild let:builder>
          <Button builders={[builder]} variant='outline' disabled={targetsAreZero}>
            Schedule
          </Button>
        </Popover.Trigger>
        <Popover.Content class="flex flex-col gap-4">
          <div class="flex flex-row justify-between">
            <div class="space-y-1">
              <Label for="selected-date">Date</Label>
              <Input type="date" id="selected-date" bind:value={inputDate} disabled />
            </div>
            <div class="space-y-1">
              <Label for="selected-time">Time</Label>
              <Input type="time" id="selected-time" bind:value={inputTime} />
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <Button size="sm" variant="outline" on:click={() => {workoutSchedulePopoverOpen = false}}>
              Cancel
            </Button>
            <Button size="sm" variant="outline" on:click={() => {scheduleMagicWorkout(new Date(`${inputDate}T${inputTime}`)); workoutSchedulePopoverOpen = false;}}>
              Schedule
            </Button>
          </div>
        </Popover.Content>
      </Popover.Root>
    {/if}
  {:else}
    <Button variant="outline" disabled={targetsAreZero} on:click={() => (workoutDialogOpen = true)}>Generate Workout</Button>

    <Dialog.Root bind:open={workoutDialogOpen} portal="#shadcn-ui-dialog-target">
      <Dialog.Content class="flex flex-col max-h-[95dvh] max-w-[660px] w-[95vw] p-0 overflow-hidden rounded-xl">
        <!-- Header -->
        <!-- <div class="flex items-start gap-4 pl-5 pt-4 pb-3 pr-8 border-b border-border">
          <div class="flex-1 min-w-0">
            <h2 class="text-xl font-semibold leading-tight truncate" title={exercise?.name}>
              {#if Activity.getDetailsPageLink(exercise)}
                <a class='text-foreground hover:underline' href={Activity.getDetailsPageLink(exercise)} target={Activity.isWorkout(exercise) ? "workout" : "activity"}>
                  {exercise.name}
                </a>
              {:else}
                {exercise.name}
              {/if}
            </h2>
            <div class="mt-1 flex flex-wrap gap-2 items-center text-xs text-muted-foreground">
              {#if durationStr}<span class="inline-flex items-center gap-1"><i class="fa-regular fa-clock text-[10px]"></i>{durationStr}</span>{/if}
              {#if distanceVal > 0}<span class="inline-flex items-center gap-1"><i class="fa-regular fa-road text-[10px]"></i>{distanceVal.toFixed(1)} {distanceUnit}</span>{/if}
            </div>
          </div>
        </div> -->

        <div class="flex-1 overflow-y-auto px-5 pb-5 pt-4 space-y-6">
          <MagicBucketsGenerateWorkout bind:fields={magicBucketsFieldsAndData} {targetsAreLowIntensity} />
        </div>

        <!-- Footer actions -->
        <div class="flex flex-row flex-wrap w-full justify-end gap-3 px-5 py-4 border-t border-border bg-muted/30">
          {#if forecastActivity}
            <Button variant="outline" on:click={assignMagicWorkoutToForecast}>Assign to Forecast</Button>
          {:else}
            <Popover.Root bind:open={workoutSchedulePopoverOpen} portal="#shadcn-ui-dialog-target">
              <Popover.Trigger class={cn(buttonVariants({ variant: "outline" }))}>
                Schedule
              </Popover.Trigger>
              <Popover.Content class="flex flex-col gap-4">
                <div class="flex flex-row justify-between">
                  <div class="space-y-1">
                    <Label for="selected-date">Date</Label>
                    <Input type="date" id="selected-date" bind:value={inputDate} disabled />
                  </div>
                  <div class="space-y-1">
                    <Label for="selected-time">Time</Label>
                    <Input type="time" id="selected-time" bind:value={inputTime} />
                  </div>
                </div>
                <div class="flex justify-end gap-2">
                  <Button size="sm" variant="outline" on:click={() => {workoutSchedulePopoverOpen = false}}>
                    Cancel
                  </Button>
                  <Button size="sm" variant="outline" on:click={() => {scheduleMagicWorkout(new Date(`${inputDate}T${inputTime}`)); workoutSchedulePopoverOpen = false;}}>
                    Schedule
                  </Button>
                </div>
              </Popover.Content>
            </Popover.Root>
          {/if}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  {/if}
</div>
